// Recordings - the platform's home page: list + upload + live processing status.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate } from 'react-router-dom';
import lpsService from '../services/lpsService';
import SessionAnalytics from '../components/SessionAnalytics';

const STATUS_COLOR = {
  queued: 'default', transcribing: 'info', diarizing: 'info',
  analyzing: 'warning', done: 'success', failed: 'error',
};

const DEPARTMENTS = ['General', 'Mental Health', 'Legal', 'Management', 'Security', 'HR'];

export default function RecordingsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('General');
  const [subjectId, setSubjectId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSessions, setProgressSessions] = useState(null);
  const [progressSubject, setProgressSubject] = useState('');
  const navigate = useNavigate();
  const timer = useRef(null);

  const openProgress = async (subject) => {
    setProgressSubject(subject); setProgressSessions(null); setProgressOpen(true);
    try {
      const d = await lpsService.getLongitudinal(subject);
      setProgressSessions(d.sessions || []);
    } catch { setProgressSessions([]); }
  };

  const refresh = useCallback(() => {
    lpsService.listRecordings().then(setRows).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    timer.current = setInterval(refresh, 3000);
    return () => clearInterval(timer.current);
  }, [refresh]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await lpsService.uploadRecording(file, { title, department, subject_id: subjectId });
      setOpen(false); setFile(null); setTitle(''); setSubjectId('');
      refresh();
    } finally {
      setUploading(false);
    }
  };

  const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : '—');

  const visible = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    if (q && !(`${r.title} ${r.subject_id || ''} ${r.uploader_name || ''}`.toLowerCase().includes(q))) return false;
    if (filterDept && r.department !== filterDept) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    return true;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Recordings</Typography>
          <Typography variant="body2" color="text.secondary">
            Every conversation is processed inside your environment — nothing leaves it.
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setOpen(true)}>
          Upload recording
        </Button>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} className="no-print">
        <TextField size="small" placeholder="Search title, subject ID, uploader…"
          value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 300 }} />
        <TextField select size="small" label="Department" value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All departments</MenuItem>
          {[...new Set(rows.map((r) => r.department).filter(Boolean))].map((d) => (
            <MenuItem key={d} value={d}>{d}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Status" value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">Any status</MenuItem>
          {['done', 'queued', 'transcribing', 'diarizing', 'analyzing', 'failed'].map((st) => (
            <MenuItem key={st} value={st}>{st}</MenuItem>
          ))}
        </TextField>
        {(search || filterDept || filterStatus) && (
          <Button size="small" onClick={() => { setSearch(''); setFilterDept(''); setFilterStatus(''); }}>
            Clear ({visible.length}/{rows.length})
          </Button>
        )}
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Subject ID</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell align="right">Report</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{r.title}</TableCell>
                <TableCell>{r.department}</TableCell>
                <TableCell>{r.subject_id || '—'}</TableCell>
                <TableCell>{fmtDur(r.duration_seconds)}</TableCell>
                <TableCell>
                  <Chip size="small" label={r.status} color={STATUS_COLOR[r.status] || 'default'} />
                  {['transcribing', 'diarizing', 'analyzing'].includes(r.status) && (
                    <LinearProgress sx={{ mt: 0.5, width: 90 }} />
                  )}
                </TableCell>
                <TableCell>{(r.created_at || '').slice(0, 16).replace('T', ' ')}</TableCell>
                <TableCell align="right">
                  {r.subject_id && rows.filter((x) => x.subject_id === r.subject_id && x.status === 'done').length >= 2 && (
                    <Button size="small" startIcon={<TimelineIcon />}
                      onClick={() => openProgress(r.subject_id)}>
                      Progress
                    </Button>
                  )}
                  <Button size="small" startIcon={<DescriptionIcon />} disabled={r.status !== 'done'}
                    onClick={() => navigate(`/recordings/${r.id}`)}>
                    Open
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!visible.length && (
              <TableRow><TableCell colSpan={7}>
                <Typography sx={{ p: 3 }} color="text.secondary" align="center">
                  No recordings match — adjust search or upload one.
                </Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={progressOpen} onClose={() => setProgressOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Progress — subject {progressSubject}</DialogTitle>
        <DialogContent>
          {progressSessions === null && <LinearProgress sx={{ my: 3 }} />}
          {progressSessions !== null && progressSessions.length >= 2 && (
            <SessionAnalytics longitudinal={progressSessions}
              lang={(progressSessions[0]?.locale || '').startsWith('he') ? 'he' : 'en'} />
          )}
          {progressSessions !== null && progressSessions.length < 2 && (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              Need at least two completed sessions with analytics for a progress view.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgressOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload recording</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label">
              {file ? file.name : 'Choose audio file (wav / mp3 / m4a…)'}
              <input hidden type="file" accept="audio/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Button>
            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
            <TextField select label="Department" value={department}
              onChange={(e) => setDepartment(e.target.value)} fullWidth>
              {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <TextField label="Subject / Patient ID (optional)" value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)} fullWidth
              helperText="Visible only to roles with identifier permission" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!file || uploading} onClick={upload}>
            {uploading ? 'Uploading…' : 'Upload & process'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
